import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { operatorKeys } from '~/lib/query-keys';

export type OperatorRunStatus = 'running' | 'succeeded' | 'failed' | 'stopped';

/**
 * FRONT-1989: one step of a `dispatch_desktop_task` run, as the backend's
 * "Agreed seam with step 6" wire contract shapes it (`src/operator/store.ts`
 * `toRunWire`). `screenshotUrl`, when present, is a same-origin path the
 * card's `<img>`/lightbox can point at directly
 * (`/api/operator/runs/:runId/steps/:index/screenshot`) — never inline image
 * bytes.
 */
export interface OperatorRunStepWire {
  index: number;
  at: string;
  action: string;
  label: string;
  note: string | null;
  screenshotUrl: string | null;
  /** FRONT-1996 F14 — set ONLY for a `human_takeover` step (read from that
   *  step's own redacted `args`, see `src/operator/store.ts`'s
   *  `takeoverStepFields`); `null` for every other action. */
  takeoverDurationMs: number | null;
  takeoverRequestedBy: OperatorRunTakeoverRequestedBy | null;
}

/**
 * FRONT-1996 "Take the wheel" — a person in control of the desktop, either
 * because Astra asked (`ask_for_human`, `requestedBy: 'operator'`) or the
 * owner pressed "Take over" unprompted (`requestedBy: 'owner'`). Mirrors
 * `src/operator/store.ts`'s `OperatorRunTakeoverWire` byte-for-byte — ISO
 * string timestamps, never `Date`s, same as every other timestamp on this
 * wire.
 */
export type OperatorRunTakeoverState = 'requested' | 'active';
export type OperatorRunTakeoverRequestedBy = 'operator' | 'owner';

export interface OperatorRunTakeoverWire {
  state: OperatorRunTakeoverState;
  requestedBy: OperatorRunTakeoverRequestedBy;
  reason: string | null;
  requestedAt: string;
  startedAt: string | null;
}

/** The list endpoint's row shape — mirrors the backend's `OperatorRunListWire`
 *  (`src/operator/store.ts`). Never selected with `steps`, and — FRONT-2185 —
 *  never with `sessionId` either: that field is added on the DETAIL wire only
 *  (below), so keeping this interface separate rather than deriving it via
 *  `Omit<OperatorRunWire, 'steps'>` is what stops a future field added to the
 *  detail shape from silently also claiming the list endpoint returns it. */
export interface OperatorRunListWire {
  id: string;
  status: OperatorRunStatus;
  goal: string;
  model: string;
  servedModel: string | null;
  summary: string | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  takeover: OperatorRunTakeoverWire | null;
  /** FRONT-2341: ISO timestamp of when a stop was requested for this run
   *  (the backend's `requestStop`/stop service), or `null` if none was.
   *  Distinct from `status` — a run keeps reading `"running"` for a little
   *  while after this is set, until the loop notices at its next checkpoint
   *  (or the stop-watch aborts it) and actually finishes as `"stopped"` —
   *  this is what lets the Stop control show "Stopping…"/"Stop again"
   *  instead of a control that silently does nothing. See
   *  `features/operator/stop-state.ts`. */
  stopRequestedAt: string | null;
}

/**
 * FRONT-1989: the full run detail object `GET /api/operator/runs/:runId`
 * returns — the frozen seam shared with the parallel VNC/operator-panel
 * (step 6) session. Do not change this shape without re-checking that seam.
 *
 * `takeover` (FRONT-1996) is `null` whenever no takeover is in progress —
 * the backend clears all five of its own takeover columns together on
 * `finishRun`, so a finished run's `takeover` is always `null` too.
 *
 * `sessionId` (FRONT-2185) — the chat session this task was dispatched from,
 * read by the operator panel's Chat tab to mount the real conversation.
 * Detail-only, see {@link OperatorRunListWire}'s own doc comment.
 */
export interface OperatorRunWire extends OperatorRunListWire {
  sessionId: string;
  steps: OperatorRunStepWire[];
}

/** The list endpoint's row shape — same run fields, minus `steps` (never
 *  selected in a list read per the final plan's store contract) and minus
 *  `sessionId` (detail-only, see {@link OperatorRunWire}'s own doc comment). */
export type OperatorRunSummaryWire = OperatorRunListWire;

interface OperatorRunListResponse {
  runs: OperatorRunSummaryWire[];
}

/**
 * Whether {@link useOperatorRun} should keep polling. A run still `running`
 * has no per-step SSE event of its own carrying a step index the poller can
 * diff against — `operator-run-updated` only ever triggers an invalidate
 * (this file's own contract, mirrored in `use-sse.ts`) — so a 3s poll is the
 * fallback for a tab with no live SSE subscriber, same reasoning as
 * `researchRefetchInterval` in `use-research.ts`. Exported so the interval
 * decision is unit-testable without mounting `useQuery`.
 */
export function operatorRunRefetchInterval(run: OperatorRunWire | undefined): number | false {
  return run?.status === 'running' ? 3_000 : false;
}

/**
 * The single-run detail query a `DesktopTaskCard` polls once it knows its
 * own `runId` (from the tool's output, or from {@link useOperatorRunForToolCall}
 * below). `operator-run-updated` (`use-sse.ts`) invalidates
 * `operatorKeys.run(runId)` on every step/status change, so the poll above is
 * a fallback, not the primary refresh path.
 */
export function useOperatorRun(runId: string | undefined) {
  return useQuery({
    queryKey: runId ? operatorKeys.run(runId) : operatorKeys.runs(),
    queryFn: async (): Promise<OperatorRunWire> =>
      api.get<OperatorRunWire>(`/api/operator/runs/${encodeURIComponent(runId as string)}`),
    enabled: !!runId,
    refetchInterval: (q) => operatorRunRefetchInterval(q.state.data),
  });
}

/**
 * FRONT-1989 review round 1 (F2): whether {@link useOperatorRunForToolCall}
 * should keep polling. A card mounted before the run row exists (the tool
 * call was just approved, so the loop hasn't created it yet) has nothing to
 * invalidate it — `operator-run-updated` targets an existing run id, so
 * there is no SSE event for "a run now exists for this toolCallId" — which
 * left this lookup stuck on its first "no run yet" answer forever. Same
 * "poll while empty" shape as {@link operatorRunRefetchInterval} above.
 */
export function operatorRunLookupRefetchInterval(
  result: OperatorRunSummaryWire | null | undefined,
): number | false {
  return result ? false : 3_000;
}

/**
 * One-time lookup a freshly-mounted `DesktopTaskCard` uses to learn its own
 * run id when the tool call's `output` hasn't carried one yet (e.g. a page
 * reload landed mid-run, before the tool result was ever streamed to this
 * tab). Returns the newest run for this exact `(sessionId, toolCallId)` pair,
 * or `null` when none exists yet. Callers pass the result's `id` into
 * {@link useOperatorRun} for the full, step-carrying detail — this endpoint
 * never returns steps (`OperatorRunSummaryWire`).
 */
export function useOperatorRunForToolCall(
  sessionId: string | undefined,
  toolCallId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey:
      sessionId && toolCallId
        ? operatorKeys.forToolCall(sessionId, toolCallId)
        : operatorKeys.lookups(),
    queryFn: async (): Promise<OperatorRunSummaryWire | null> => {
      const params = new URLSearchParams({
        sessionId: sessionId as string,
        toolCallId: toolCallId as string,
        limit: '1',
      });
      const data = await api.get<OperatorRunListResponse>(`/api/operator/runs?${params.toString()}`);
      return data.runs[0] ?? null;
    },
    enabled: enabled && !!sessionId && !!toolCallId,
    refetchInterval: (q) => operatorRunLookupRefetchInterval(q.state.data),
  });
}
